import { ColumnAggregationType } from "utils/enums";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { T } from "services/lang/LanguageService";
import { SortDirection } from "gql/generated/types";
import { DataTable, DataTableValue } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import { Row } from "primereact/row";
import useSafeId from "hooks/dashboard/useDashboardComponentId";
import React, {
  CSSProperties,
  JSX,
  WheelEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import DataCell from "./components/templates/table-components/data-cell/DataCell";
import AttributeHeader from "./components/templates/table-components/header/AttributeHeader";
import {
  IColumnsWithValuesProps,
  IProductTemplateAttribute,
  ITableDataEntry,
  ProductTemplateNonTransposedTableProps,
} from "./utils/interfaces";

import {
  checkForHighlightType,
  checkForUnwantedType,
  compareValues,
  formatDataTotalsAggregation,
  getHighlightColor,
  getValueColors,
  handlePrecision,
} from "./utils/utils";
import "./ProductTemplateTable.scss";

export default function ProductTemplateTableDefault({
  templateData,
  configData,
  selectedEntryBody,
  selectedEntryId,
  selectedEntryIndex,
  itemsPerPage,
  editedDataHandler,
  onClickSign,
  handleTableKeyDown,
  EditRowDataCell,
  reportOptions,
  editColumnLocation = "BACK",
  page,
  setPage,
  allowRowEdit,
  foreignKeyWrap,
  showTotals,
  hidePagination,
  isColumnsSortable,
  sortedData,
  setSortedData
}: ProductTemplateNonTransposedTableProps) {
  const tableId = useSafeId();
  const divRef = useRef<HTMLDivElement>(null);

  const [selected, setSelected] = useState<DataTableValue | null>(null);
  const [rows, setRows] = useState(itemsPerPage);
  const [aggregatedColumns, setAggregatedColumns] = useState<IColumnsWithValuesProps[]>([]);

  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortDirection>(SortDirection.Asc);

  const rowsPerPageOptions = itemsPerPage
    ? [itemsPerPage, 10, 20, 30].sort((a, b) => a - b).filter((x, i, a) => a.indexOf(x) === i)
    : [];

  const handleWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
    if (event.altKey) {
      const div: HTMLDivElement = event.currentTarget as HTMLDivElement;
      div.scrollLeft += event.deltaY * 0.45;
    }
  }, []);

  const sortTableData = (
    data: ITableDataEntry[],
    field: string,
    order: SortDirection
  ): ITableDataEntry[] => {
    const direction = order === SortDirection.Asc ? 1 : -1;

    return [...data].sort((a, b) => {
      const valA = a.entries[field]?.value;
      const valB = b.entries[field]?.value;
      return direction * compareValues(valA, valB);
    });
  };

  const handleSort = (field: string) => {
    let newOrder = SortDirection.Asc;
    if (sortField === field) {
      if (sortOrder === SortDirection.Asc) {
        newOrder = SortDirection.Desc;
      } else {
        newOrder = SortDirection.Asc;
      }
    }

    setSortField(field);
    setSortOrder(newOrder);
    const sorted = sortTableData(templateData, field, newOrder);
    setSortedData(sorted);
  };

  const dataCell = (
    val: ITableDataEntry,
    index: number,
    configVal: IProductTemplateAttribute,
    configIndex: number
  ) => {
    const dataValue = selectedEntryBody && selectedEntryIndex === index ? selectedEntryBody : val;
    const obj = dataValue.entries[configVal.referenceName];

    let color;
    let bgColor;
    if (obj && obj.type && checkForHighlightType(obj.type)) {
      [color, bgColor] = getValueColors(obj.value as number, {
        highlightRanges: configVal.bgHighlightRanges,
        defaultHighlightColor: configVal.highlightColor,
      });
    } else {
      [color, bgColor] = getHighlightColor(configVal.highlightColor);
    }

    return (
      <div className="text-center" style={styleHandler(color, bgColor, obj && obj.type)}>
        <DataCell
          key={val.id}
          headerConfig={configVal}
          dataValue={dataValue}
          indexHeader={configIndex}
          indexEntry={index}
          changeHandler={editedDataHandler}
          isInEditMode={selectedEntryId === val.id}
          onClickSign={onClickSign}
          foreignKeyWrap={foreignKeyWrap}
          editRequireRoles={configVal.editRequireRoles ?? []}
        />
      </div>
    );
  };

  const styleHandler = (
    color: string | undefined,
    bgColor: string | undefined,
    type: string | undefined
  ): CSSProperties | undefined => {
    if (checkForUnwantedType(type)) {
      if (color && bgColor) {
        return {
          overflowX: "auto",
          color,
          backgroundColor: bgColor,
        };
      }
      if (bgColor) {
        return {
          overflowX: "auto",
          backgroundColor: bgColor,
        };
      }

      if (color) {
        return {
          overflowX: "auto",
          color,
        };
      }
    }

    return {
      overflowX: "auto",
    };
  };

  const editColumn = (frozen = false) => (
    <Column
      key={12}
      className="non-printable"
      frozen={frozen}
      body={(rowData, options) => EditRowDataCell(rowData, options.rowIndex)}
      style={{ width: "0" }}
      hidden={!allowRowEdit}
    />
  );

  const generateFooterColumns = (aggregatedCols: IColumnsWithValuesProps[]) => {
    return aggregatedCols.map(col => {
      let tooltipText = "";
      let footerText: string | number = "";
      if (col.aggregationType === ColumnAggregationType.SUM) {
        tooltipText = T("word.sum");
        footerText = `${handlePrecision(col.total!, col.decimalPlaces!, true)} ${col.unit ?? ""}`;
      } else if (col.aggregationType === ColumnAggregationType.MEAN) {
        tooltipText = T("word.average");
        footerText = `${handlePrecision(col.total!, col.decimalPlaces!, true)} ${col.unit ?? ""}`;
      } else if (col.aggregationType === ColumnAggregationType.COUNT) {
        tooltipText = T("action.count");
        footerText = handlePrecision(col.total!, col.decimalPlaces!, true);
      }
      return (
        <Column
          key={col.referenceName}
          footer={
            <div title={tooltipText} className="text-center">
              {footerText}
            </div>
          }
        />
      );
    });
  };

  let footerGroup = null;
  if (showTotals && aggregatedColumns.length > 0) {
    footerGroup = (
      <ColumnGroup>
        <Row>{generateFooterColumns(aggregatedColumns)}</Row>
      </ColumnGroup>
    );
  }

  useEffect(() => {
    if (reportOptions && document.getElementsByClassName("print-mode-enabled").length > 0) {
      const table = document.querySelector(`#${tableId}`);
      if (table) {
        const allChildren = table.querySelectorAll("*");

        if (reportOptions.fontSize) {
          for (let i = 0; i < allChildren.length; i++) {
            allChildren[i].style.fontSize = reportOptions?.fontSize;
          }
        }
      }
    }
  }, [footerGroup]);

  useEffect(() => {
    setAggregatedColumns(formatDataTotalsAggregation(configData, templateData));
  }, [configData?.data?.attributes, templateData]);

  const dynamicColumns = configData?.data?.attributes.map((col, i) => {
    const isSorted = sortField === col.referenceName;
    let sortIcon: string;

    if (isSorted) {
      sortIcon = sortOrder === SortDirection.Asc ? "amount-up-alt" : "amount-down";
    } else {
      sortIcon = "alt";
    }

    return (
      <Column
        key={col.referenceName}
        field={col.referenceName}
        align="center"
        className="change-padding"
        header={
          <div
            onClick={() => isColumnsSortable && handleSort(col.referenceName)}
            className={`${isColumnsSortable && "column-header-sortable"}`}
          >
            <AttributeHeader key={col.referenceName} attribute={col} noTh />
            {isColumnsSortable && <i className={`pi pi-sort-${sortIcon}`} />}
          </div>
        }
        body={(rowData, options) => dataCell(rowData, options.rowIndex, col, i)}
      />
    );
  });

  const allColumns = () => {
    const columns: JSX.Element[] = [...dynamicColumns!];

    if (editColumnLocation === "FRONT") {
      columns.unshift(editColumn(true));
    } else if (editColumnLocation === "BACK") {
      columns.push(editColumn(false));
    } else {
      console.error(T("error.invalid-edit-column-location"));
    }

    return columns;
  };
  return (
    <>
      <div
        className="default-table"
        id={tableId}
        style={{ overflowX: "auto" }}
        onKeyDown={e => handleTableKeyDown(e.key)}
        ref={divRef}
        onWheel={handleWheel}
      >
        <DataTable
          value={rows ? [...sortedData.slice(page, page + rows)] : sortedData}
          size="small"
          showGridlines
          selectionMode="single"
          selection={selected}
          onSelectionChange={e => setSelected(e.value)}
          footerColumnGroup={footerGroup}
        >
          {allColumns()}
        </DataTable>
      </div>
      {!!itemsPerPage && !hidePagination && (
        <Paginator
          totalRecords={templateData.length}
          rows={rows}
          first={page}
          onPageChange={e => {
            setPage(e.first);
            setRows(e.rows);
          }}
          template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate={`${T("word.showing")} {first} ${T("word.to")} {last} ${T("word.of")} {totalRecords}`}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </>
  );
}
